import { Alert, Box, Stack } from '@mui/material';
import FullResearchDialog from 'features/lead-list-research/lead-list-storage-detail/full-research-dialog';
import LeadListDetailsHeader from 'features/lead-list-research/lead-list-storage-detail/lead-list-details-header';
import LeadListEnrichmentDialog from 'features/lead-list-research/lead-list-storage-detail/lead-list-enrichment-dialog';
import LeadsTable from 'features/lead-list-research/lead-list-storage-detail/leads-table';
import RefineLeadListDialog from 'features/lead-list-research/lead-list-storage-detail/refine-lead-list-dialog';
import ConfirmDeleteLeadListDialog from 'features/lead-list-research/lead-list-storage/confirm-delete-lead-list-dialog';
import PaiSpinningLoader from 'features/ui/base-components/pai-spinning-loader';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import AddLeadListToFunnelDialog from '../features/lead-list-research/lead-list-storage-detail/add-lead-list-to-funnel-dialog';
import { useUserData } from 'hooks/use-user-data';
import {
  useGetLeadListById,
  useLeadListStatus,
  StoredLeadList,
  useProcessRefinementWithPrevious,
  useProcessFullResearch,
  exportLeadListAsCSV,
  useEnrichLeadListWithLinkedinUrl,
  useBetterContactEnrichmentStatus,
  useEnrichContactsWithBetterContact,
  useGenerateMatrixSubprompts,
  useUpdateMatrixSubprompts,
  ApprovedSubregion,
  ApprovedSubsearch,
  ApprovedPeopleSearch,
  addLeadListContactsToFunnel,
  AddToFunnelResponse,
  useRemoveContactsFromLeadList,
} from '../services/lead-list-research.service';
import useAppBarNavigationStore, { NavigationStates } from '../stores/app-bar-navigation-store';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import PromptApprovalDialog from 'features/lead-list-research/lead-list-storage-detail/prompt-approval-dialog';

const emptyLeadList: StoredLeadList = {
  _id: '',
  name: '',
  product: '',
  region: '',
  creator: '',
  leadData: [],
  isRefined: false,
  metadata: {
    createdAt: '',
    leadCount: 0,
    autoSaved: false,
  },
  createdAt: '',
  updatedAt: '',
};

const LeadListStorageDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { setActiveTab } = useAppBarNavigationStore();
  const { data: apiResponse, isLoading: isLoadingFull, error: errorFull, refetch: refetchFull } = useGetLeadListById(id || '');
  const { data: statusData, isLoading: isLoadingStatus, error: errorStatus } = useLeadListStatus(id || '');
  const { mutate: processRefinementWithPrevious, isPending: isRefining } = useProcessRefinementWithPrevious();
  const { mutate: enrichListWithLinkedin, isPending: isEnrichingLinkedIn, error: enrichLinkedInError } = useEnrichLeadListWithLinkedinUrl();
  const { t } = useTranslation();
  const { mutate: enrichContactsWithBetterContact } = useEnrichContactsWithBetterContact();
  const { mutate: removeContactsFromLeadList } = useRemoveContactsFromLeadList();
  const { token } = useUserData();

  // Dialog and enrichment state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [refinementDialogOpen, setRefinementDialogOpen] = useState(false);
  const [fullResearchDialogOpen, setFullResearchDialogOpen] = useState(false);
  const [enrichDialogOpen, setEnrichDialogOpen] = useState(false);
  const [customField, setCustomField] = useState('');
  const [customFieldPrompt, setCustomFieldPrompt] = useState('');
  const [isAddToFunnelDialogOpen, setIsAddToFunnelDialogOpen] = useState(false);

  // Research/refinement state
  const [refinementFeedback, setRefinementFeedback] = useState('');
  const [autoSave, setAutoSave] = useState(true);
  const [numRegions, setNumRegions] = useState<number>(6);
  const [numSubsearches, setNumSubsearches] = useState<number>(5);
  const [editablePrompt, setEditablePrompt] = useState('');
  const [editableProduct, setEditableProduct] = useState('');
  const [editableRegion, setEditableRegion] = useState('');
  const [refineRegionSplitInstructions, setRefineRegionSplitInstructions] = useState('');
  const [refineSubsearchInstructions, setRefineSubsearchInstructions] = useState('');
  const [peopleSearchInstructions, setPeopleSearchInstructions] = useState('');
  const [numPeopleSearches, setNumPeopleSearches] = useState<number>(3);

  // BetterContact enrichment state
  type EnrichmentStep = 'idle' | 'selecting' | 'enriching';
  const [enrichmentStep, setEnrichmentStep] = useState<EnrichmentStep>('idle');
  const [bcSelectedIndices, setBcSelectedIndices] = useState<number[]>([]);

  // Remove contacts state
  type RemovalStep = 'idle' | 'selecting' | 'removing';
  const [removalStep, setRemovalStep] = useState<RemovalStep>('idle');
  const [removeSelectedIndices, setRemoveSelectedIndices] = useState<number[]>([]);

  const bcStatusQuery = useBetterContactEnrichmentStatus(
    id || '',
    bcSelectedIndices,
    enrichmentStep === 'enriching'
  );

  // Prompt approval state
  const [promptApprovalOpen, setPromptApprovalOpen] = useState(false);
  const [matrixId, setMatrixId] = useState<string | null>(null);
  const [approvedSubregions, setApprovedSubregions] = useState<ApprovedSubregion[]>([]);
  const [approvedSubsearches, setApprovedSubsearches] = useState<ApprovedSubsearch[]>([]);
  const [approvedPeopleSearches, setApprovedPeopleSearches] = useState<ApprovedPeopleSearch[]>([]);

  const { mutate: generateMatrixSubprompts, isPending: isGeneratingMatrix } = useGenerateMatrixSubprompts();
  const { mutate: updateMatrixSubprompts, isPending: isUpdatingMatrix } = useUpdateMatrixSubprompts();
  const { mutate: processFullResearch } = useProcessFullResearch();

  // New state for Process Full Research Directly
  const [isProcessingFullDirect, setIsProcessingFullDirect] = useState(false);

  // Set the active tab when component mounts
  useEffect(() => {
    setActiveTab(NavigationStates.LEAD_LIST_RESEARCH);
  }, [setActiveTab]);

  // Use correct extraction for data and leads from API response, supporting both wrapped and unwrapped responses
  const data = (apiResponse && (apiResponse as any).data) ? (apiResponse as any).data : apiResponse || emptyLeadList;
  const leads = data.leadData || [];

  // Use status from storage API response for UI logic
  const jobStatus = data.status;

  // Determine if any LinkedIn enrichment is running (mutation pending or backend processing)
  const linkedInEnrichmentRunning = isEnrichingLinkedIn || jobStatus === 'in_progress';

  // Automatically fetch the full LeadList when statusData becomes 'done' or 'matrix_ready'
  useEffect(() => {
    if (statusData?.data?.status === 'done' || statusData?.data?.status === 'matrix_ready') {
      refetchFull();
    }
  }, [statusData, refetchFull]);

  // Dialog handlers
  const handleOpenRefinementDialog = () => {
    setEditableProduct(data.product || '');
    setEditableRegion(data.region || '');
    setEditablePrompt(data.metadata?.originalPrompt || '');
    setRefineRegionSplitInstructions(
      data.regionSplitInstructions ||
      data.settings?.regionSplitInstructions ||
      data.metadata?.regionSplitInstructions ||
      ''
    );
    setRefineSubsearchInstructions(
      data.subsearchInstructions ||
      data.settings?.subsearchInstructions ||
      data.metadata?.subsearchInstructions ||
      ''
    );
    setPeopleSearchInstructions(
      data.peopleSearchInstructions ||
      data.settings?.peopleSearchInstructions ||
      data.metadata?.peopleSearchInstructions ||
      ''
    );
    setRefinementDialogOpen(true);
  };
  const handleCloseRefinementDialog = () => setRefinementDialogOpen(false);
  const handleOpenFullResearchDialog = () => {
    setEditableProduct(data.product || '');
    setEditableRegion(data.region || '');
    setEditablePrompt(data.metadata?.originalPrompt || '');
    setRefineRegionSplitInstructions(
      data.regionSplitInstructions ||
      data.settings?.regionSplitInstructions ||
      data.metadata?.regionSplitInstructions ||
      ''
    );
    setRefineSubsearchInstructions(
      data.subsearchInstructions ||
      data.settings?.subsearchInstructions ||
      data.metadata?.subsearchInstructions ||
      ''
    );
    setPeopleSearchInstructions(
      data.peopleSearchInstructions ||
      data.settings?.peopleSearchInstructions ||
      data.metadata?.peopleSearchInstructions ||
      ''
    );
    setNumRegions(data.numRegions || 6);
    setNumSubsearches(data.numSubsearches || 5);
    setNumPeopleSearches(data.numPeopleSearches || 3);
    setFullResearchDialogOpen(true);
  };
  const handleCloseFullResearchDialog = () => setFullResearchDialogOpen(false);
  const handleOpenConfirmDeleteLeadListDialog = () => setDeleteConfirmOpen(true);
  const handleCloseConfirmDeleteLeadListDialog = () => setDeleteConfirmOpen(false);
  const handleOpenAddToFunnelDialog = () => setIsAddToFunnelDialogOpen(true);
  const handleCloseAddToFunnelDialog = () => setIsAddToFunnelDialogOpen(false);
  const handleEnrichLeadList = () => setEnrichDialogOpen(true);
  const handleCloseEnrichDialog = () => {
    setEnrichDialogOpen(false);
    setCustomField('');
    setCustomFieldPrompt('');
  };

  // Submit handlers (stubs, should be implemented as needed)
  const handleSubmitRefinement = () => {
    if (!id) return;
    processRefinementWithPrevious(
      {
        feedback: refinementFeedback,
        savedListId: id,
        product: editableProduct,
        region: editableRegion,
        prompt: editablePrompt,
        autoSave,
        regionSplitInstructions: refineRegionSplitInstructions,
        subsearchInstructions: refineSubsearchInstructions,
        peopleSearchInstructions: peopleSearchInstructions,
        numPeopleSearches,
      },
      {
        onSuccess: () => {
          setRefinementDialogOpen(false);
          refetchFull();
        },
        onError: (error) => {
          alert('Failed to refine lead list. Please try again.');
          console.error(error);
        },
      }
    );
  };
  const handleSubmitFullResearch = () => {
    generateMatrixSubprompts(
      {
        leadListId: id,
        product: editableProduct,
        region: editableRegion,
        numRegions,
        numSubsearches,
        numCompaniesPerSubregion: 10,
        subsearchInstructions: refineSubsearchInstructions,
        peopleSearchInstructions: peopleSearchInstructions,
        mainPrompt: editablePrompt,
      },
      {
        onSuccess: (resp: any) => {
          setMatrixId(resp.data.id);
          setApprovedSubregions(resp.data.approvedSubregions);
          setApprovedSubsearches(resp.data.approvedSubsearches);
          setApprovedPeopleSearches(resp.data.approvedPeopleSearches);
          setFullResearchDialogOpen(false);
          setPromptApprovalOpen(true);
        },
        onError: (error: any) => {
          alert('Failed to generate prompts. Please try again.');
          console.error(error);
        },
      }
    );
  };
  const handleSubmitEnrichDialog = () => {
    if (!id) return;
    enrichListWithLinkedin(
      { id, customField, customFieldPrompt },
      {
        onSuccess: () => {
          handleCloseEnrichDialog();
          refetchFull();
        },
        onError: (error) => {
          console.error('LinkedIn Enrichment failed', error);
        },
      }
    );
  };
  const handleExportToExcel = () => {
    if (!leads || leads.length === 0) return;
    // Expanded columns to include all fields from the sample
    const columns = [
      'contact.fullName',
      'contact.firstName',
      'contact.lastName',
      'contact.role',
      'contact.email',
      'contact.phone',
      'contact.linkedinUrl',
      'company.name',
      'company.website',
      'company.industry',
      'company.size',
      'company.address.street',
      'company.address.postalCode',
      'company.address.city',
      'company.address.country',
      'company.reason',
      'company.linkedinUrl',
      'contactLinkedinUrl',
      'companyLinkedinUrl',
      'leadReason',
      'discoveryPrompt',
      'contactSearchPrompt',
    ];
    const worksheetData = [
      columns,
      ...leads.map((lead: any) => [
        lead.contact?.fullName || '',
        lead.contact?.firstName || '',
        lead.contact?.lastName || '',
        lead.contact?.role || '',
        lead.contact?.email || '',
        lead.contact?.phone || '',
        lead.contact?.linkedinUrl || '',
        lead.company?.name || '',
        lead.company?.website || '',
        lead.company?.industry || '',
        lead.company?.size || '',
        lead.company?.address?.street || '',
        lead.company?.address?.postalCode || '',
        lead.company?.address?.city || '',
        lead.company?.address?.country || '',
        lead.company?.reason || '',
        lead.company?.linkedinUrl || '',
        lead.contactLinkedinUrl || '',
        lead.companyLinkedinUrl || '',
        lead.leadReason || '',
        lead.discoveryPrompt || '',
        lead.contactSearchPrompt || '',
      ]),
    ];
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads');
    const fileName = `${data.name || 'lead-list'}_leads.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };
  const handleExportAsCSV = () => {
    if (!id) return;
    exportLeadListAsCSV(id);
  };
  const handleConfirmDelete = () => { };

  const handleActualAddToFunnel = async (
    funnelId: string,
    contactIndices?: number[]
  ): Promise<AddToFunnelResponse | null> => {
    if (!id || !token) return null;

    const payload: any = {
      leadListId: id,
      funnelId,
    };

    if (contactIndices && contactIndices.length > 0) {
      payload.contactIndices = contactIndices;
    }

    try {
      const { data, error } = await addLeadListContactsToFunnel(payload, token);

      if (error || !data) {
        return {
          success: false,
          message: error?.message || 'Failed to add contacts',
          funnelUpdateStatus: {
            success: false,
            countAdded: 0,
            message: error?.message || 'Failed to add contacts',
          },
          leadListId: id,
          funnelId,
        };
      }

      // Refresh lead list/funnel data if needed here
      return data;
    } catch (err: any) {
      return {
        success: false,
        message: err?.message || 'Unexpected error',
        funnelUpdateStatus: {
          success: false,
          countAdded: 0,
          message: err?.message || 'Unexpected error',
        },
        leadListId: id,
        funnelId,
      };
    }
  };

  // Handler: enrichment button click
  const handleEnrichPhoneEmailClick = () => {
    if (enrichmentStep === 'idle') {
      setEnrichmentStep('selecting');
      setBcSelectedIndices([]);
    } else if (enrichmentStep === 'selecting' && bcSelectedIndices.length > 0) {
      // Start enrichment
      if (!id) return;
      setEnrichmentStep('enriching');
      enrichContactsWithBetterContact(
        { id, contactIndices: bcSelectedIndices },
        {
          onSuccess: () => {
            // Polling will start automatically through bcStatusQuery
          },
          onError: () => {
            setEnrichmentStep('idle');
            setBcSelectedIndices([]);
          },
        }
      );
    }
  };

  // Handler: select/deselect contact
  const handleBcSelect = (index: number) => {
    setBcSelectedIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  // Handler: select/deselect contact for removal
  const handleRemoveSelect = (index: number) => {
    setRemoveSelectedIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  // Handler: removal button click
  const handleRemoveContactsClick = () => {
    if (removalStep === 'idle') {
      // Enter selecting mode, reset selection and ensure enrichment is idle
      setRemovalStep('selecting');
      setRemoveSelectedIndices([]);
      // If enrichment is in selecting mode, reset it
      if (enrichmentStep !== 'idle') {
        setEnrichmentStep('idle');
        setBcSelectedIndices([]);
      }
    } else if (removalStep === 'selecting' && removeSelectedIndices.length > 0) {
      if (!id) return;
      setRemovalStep('removing');
      removeContactsFromLeadList(
        { id, indexes: removeSelectedIndices },
        {
          onSuccess: () => {
            // After successful removal, refresh list
            setRemovalStep('idle');
            setRemoveSelectedIndices([]);
            refetchFull();
          },
          onError: () => {
            setRemovalStep('idle');
            setRemoveSelectedIndices([]);
          },
        }
      );
    }
  };

  // When enrichment is done for all selected contacts, reset
  useEffect(() => {
    if (enrichmentStep === 'enriching' && bcStatusQuery.data && bcSelectedIndices.length > 0) {
      const allDone = bcSelectedIndices.every(idx => {
        const s = bcStatusQuery.data.statuses?.find((x: any) => x.index === idx);
        return s && (s.enrichmentStatus === 'completed' || s.enrichmentStatus === 'failed');
      });
      if (allDone) {
        // Update the leads data with enriched information
        const updatedLeads = leads.map((lead: any, index: number) => {
          const status = bcStatusQuery.data.statuses?.find((s: any) => s.index === index);
          if (status && status.enrichmentStatus === 'completed') {
            return {
              ...lead,
              contact: {
                ...lead.contact,
                email: status.email || lead.contact?.email,
                phone: status.phone || lead.contact?.phone,
              }
            };
          }
          return lead;
        });

        // Update the leads data
        if (apiResponse && (apiResponse as any).data) {
          (apiResponse as any).data.leadData = updatedLeads;
        } else if (apiResponse) {
          (apiResponse as any).leadData = updatedLeads;
        }

        // Reset enrichment state after a short delay
        setTimeout(() => {
          setEnrichmentStep('idle');
          setBcSelectedIndices([]);
          refetchFull(); // Refresh the full data to ensure consistency
        }, 1500);
      }
    }
  }, [bcStatusQuery.data, enrichmentStep, bcSelectedIndices, leads, apiResponse, refetchFull]);

  // Map enrichment statuses for table
  const bcEnrichmentStatuses =
    bcStatusQuery.data && Array.isArray((bcStatusQuery.data as any).statuses)
      ? ((bcStatusQuery.data as { statuses: any[] }).statuses.reduce<{ [key: number]: any }>((acc, s) => {
        acc[s.index] = s;
        return acc;
      }, {}))
      : undefined;

  // Move prompt approval handlers above render
  const handleChangeSubregion = (idx: number, value: Partial<ApprovedSubregion>) => {
    setApprovedSubregions((prev) => prev.map((p, i) => (i === idx ? { ...p, ...value } : p)));
  };
  const handleChangeSubsearch = (idx: number, value: Partial<ApprovedSubsearch>) => {
    setApprovedSubsearches((prev) => prev.map((p, i) => (i === idx ? { ...p, ...value } : p)));
  };
  const handleChangePeopleSearch = (idx: number, value: Partial<ApprovedPeopleSearch>) => {
    setApprovedPeopleSearches((prev) => prev.map((p, i) => (i === idx ? { ...p, ...value } : p)));
  };
  const handleAddSubregion = () => setApprovedSubregions((prev) => [...prev, { name: '', description: '' }]);
  const handleAddSubsearch = () => setApprovedSubsearches((prev) => [...prev, { name: '', instruction: '' }]);
  const handleAddPeopleSearch = () => setApprovedPeopleSearches((prev) => [...prev, { name: '', instruction: '' }]);
  const handleRemoveSubregion = (idx: number) => setApprovedSubregions((prev) => prev.filter((_, i) => i !== idx));
  const handleRemoveSubsearch = (idx: number) => setApprovedSubsearches((prev) => prev.filter((_, i) => i !== idx));
  const handleRemovePeopleSearch = (idx: number) => setApprovedPeopleSearches((prev) => prev.filter((_, i) => i !== idx));

  const handleStartFullResearch = () => {
    if (!matrixId) return;
    updateMatrixSubprompts(
      {
        id: matrixId,
        approvedSubregions,
        approvedSubsearches,
        approvedPeopleSearches,
      },
      {
        onSuccess: () => {
          setPromptApprovalOpen(false);
          setTimeout(() => {
            processFullResearch(
              { savedListId: matrixId },
              {
                onSuccess: () => {
                  refetchFull();
                },
                onError: (error: any) => {
                  alert('Failed to run full research. Please try again.');
                  console.error(error);
                },
              }
            );
          }, 1200);
        },
        onError: (error: any) => {
          alert('Failed to save prompts. Please try again.');
          console.error(error);
        },
      }
    );
  };

  const handleBackToFullResearch = () => {
    setPromptApprovalOpen(false);
    setFullResearchDialogOpen(true);
  };

  const handleProcessFullDirect = () => {
    if (!id) return;
    setIsProcessingFullDirect(true);
    processFullResearch(
      { savedListId: id },
      {
        onSuccess: () => {
          setIsProcessingFullDirect(false);
          refetchFull();
        },
        onError: (error: any) => {
          setIsProcessingFullDirect(false);
          alert('Failed to run full research directly. Please try again.');
          console.error(error);
        },
      }
    );
  };

  // Show loading spinner if status is loading or not yet available
  if (isLoadingStatus || !statusData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <PaiSpinningLoader size={'medium'} />
      </Box>
    );
  }

  // Show error if status fetch failed
  if (errorStatus) {
    return <Alert severity="error">Error loading lead list status: {errorStatus instanceof Error ? errorStatus.message : 'Unknown error'}</Alert>;
  }

  // Handle failed job
  if (jobStatus === 'failed') {
    if (isLoadingFull) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <PaiSpinningLoader size={'medium'} />
        </Box>
      );
    }
    if (errorFull || !apiResponse) {
      return <Alert severity="error">Error loading lead list: {errorFull instanceof Error ? errorFull.message : 'Unknown error'}</Alert>;
    }
    let enrichPhoneEmailLabel = t('enrichPhoneEmail');
    if (enrichmentStep === 'selecting') enrichPhoneEmailLabel = t('startEnrichment');
    if (enrichmentStep === 'enriching') enrichPhoneEmailLabel = t('enriching');
    const enrichPhoneEmailDisabled =
      isEnrichingLinkedIn ||
      !leads.length ||
      (enrichmentStep === 'selecting' && bcSelectedIndices.length === 0) ||
      enrichmentStep === 'enriching' ||
      removalStep !== 'idle';

    let removeContactsLabel = t('removeContacts');
    if (removalStep === 'selecting') removeContactsLabel = `Remove ${removeSelectedIndices.length} Contacts`;
    if (removalStep === 'removing') removeContactsLabel = t('removing');

    const removeContactsDisabled =
      !leads.length ||
      (removalStep === 'selecting' && removeSelectedIndices.length === 0) ||
      removalStep === 'removing' ||
      enrichmentStep !== 'idle';

    return (
      <Stack gap={2}>
        <Alert severity="error">Research failed. Please try again or restart the research.</Alert>
        <LeadListDetailsHeader
          data={data}
          isEnriching={linkedInEnrichmentRunning}
          leads={leads}
          handleOpenFullResearchDialog={handleOpenFullResearchDialog}
          handleOpenRefinementDialog={handleOpenRefinementDialog}
          handleOpenConfirmDeleteLeadListDialog={handleOpenConfirmDeleteLeadListDialog}
          handleExportToExcel={handleExportToExcel}
          handleExportAsCSV={handleExportAsCSV}
          handleEnrichLeadList={handleEnrichLeadList}
          handleOpenAddToFunnelDialog={handleOpenAddToFunnelDialog}
          handleStartBetterContactEnrichment={handleEnrichPhoneEmailClick}
          enrichPhoneEmailLabel={enrichPhoneEmailLabel}
          enrichPhoneEmailDisabled={enrichPhoneEmailDisabled}
          handleRemoveContacts={handleRemoveContactsClick}
          removeContactsLabel={removeContactsLabel}
          removeContactsDisabled={removeContactsDisabled}
        />
        <Box sx={{ background: (theme) => theme.palette.backgrounds.main_new, padding: (theme) => theme.spacing(2, 7), borderRadius: (theme) => theme.shape.borderRadius + 'px', boxSizing: 'border-box' }}>
          {leads && leads.length > 0 ? (
            <LeadsTable
              leads={leads}
              selectionMode={enrichmentStep === 'selecting' || removalStep === 'selecting'}
              selectedIndices={enrichmentStep === 'selecting' ? bcSelectedIndices : removeSelectedIndices}
              onSelect={enrichmentStep === 'selecting' ? handleBcSelect : handleRemoveSelect}
              enrichmentStatuses={enrichmentStep === 'enriching' ? bcEnrichmentStatuses : undefined}
              showStatusColumn={enrichmentStep === 'enriching'}
              allowSelectAll={removalStep === 'selecting'}
            />
          ) : (
            <Alert severity="info">No lead data available for this list.</Alert>
          )}
        </Box>
        <RefineLeadListDialog
          open={refinementDialogOpen}
          id={id}
          isRefining={isRefining}
          refinementFeedback={refinementFeedback}
          autoSave={autoSave}
          editableProduct={editableProduct}
          editableRegion={editableRegion}
          editablePrompt={editablePrompt}
          refineRegionSplitInstructions={refineRegionSplitInstructions}
          refineSubsearchInstructions={refineSubsearchInstructions}
          peopleSearchInstructions={peopleSearchInstructions}
          setEditableProduct={setEditableProduct}
          setEditableRegion={setEditableRegion}
          setEditablePrompt={setEditablePrompt}
          setRefineRegionSplitInstructions={setRefineRegionSplitInstructions}
          setRefineSubsearchInstructions={setRefineSubsearchInstructions}
          setPeopleSearchInstructions={setPeopleSearchInstructions}
          setRefinementFeedback={setRefinementFeedback}
          setAutoSave={setAutoSave}
          handleCloseRefinementDialog={handleCloseRefinementDialog}
          handleSubmitRefinement={handleSubmitRefinement}
        />
        <FullResearchDialog
          open={fullResearchDialogOpen}
          handleCloseFullResearchDialog={handleCloseFullResearchDialog}
          handleSubmitFullResearch={handleSubmitFullResearch}
          isProcessingFull={isGeneratingMatrix}
          editableProduct={editableProduct}
          editableRegion={editableRegion}
          editablePrompt={editablePrompt}
          regionSplitInstructions={refineRegionSplitInstructions}
          subsearchInstructions={refineSubsearchInstructions}
          numRegions={numRegions}
          numSubsearches={numSubsearches}
          peopleSearchInstructions={peopleSearchInstructions}
          numPeopleSearches={numPeopleSearches}
          setEditableProduct={setEditableProduct}
          setEditableRegion={setEditableRegion}
          setEditablePrompt={setEditablePrompt}
          setNumRegions={setNumRegions}
          setNumSubsearches={setNumSubsearches}
          setPeopleSearchInstructions={setPeopleSearchInstructions}
          setNumPeopleSearches={setNumPeopleSearches}
          setRefineRegionSplitInstructions={setRefineRegionSplitInstructions}
          setRefineSubsearchInstructions={setRefineSubsearchInstructions}
          onProcessFullDirect={handleProcessFullDirect}
          isProcessingFullDirect={isProcessingFullDirect}
        />
        <PromptApprovalDialog
          open={promptApprovalOpen}
          onClose={() => setPromptApprovalOpen(false)}
          onStart={handleStartFullResearch}
          onBack={handleBackToFullResearch}
          approvedSubregions={approvedSubregions}
          approvedSubsearches={approvedSubsearches}
          approvedPeopleSearches={approvedPeopleSearches}
          onChangeSubregion={handleChangeSubregion}
          onChangeSubsearch={handleChangeSubsearch}
          onChangePeopleSearch={handleChangePeopleSearch}
          onAddSubregion={handleAddSubregion}
          onAddSubsearch={handleAddSubsearch}
          onAddPeopleSearch={handleAddPeopleSearch}
          onRemoveSubregion={handleRemoveSubregion}
          onRemoveSubsearch={handleRemoveSubsearch}
          onRemovePeopleSearch={handleRemovePeopleSearch}
          loading={isUpdatingMatrix}
        />
        <ConfirmDeleteLeadListDialog
          open={deleteConfirmOpen}
          loading={false}
          handleClose={handleCloseConfirmDeleteLeadListDialog}
          handleConfirmDelete={handleConfirmDelete}
        />
        <LeadListEnrichmentDialog
          open={enrichDialogOpen}
          isEnriching={linkedInEnrichmentRunning}
          customField={customField}
          customFieldPrompt={customFieldPrompt}
          enrichError={enrichLinkedInError?.message || null}
          setCustomField={setCustomField}
          setCustomFieldPrompt={setCustomFieldPrompt}
          handleCloseEnrichDialog={handleCloseEnrichDialog}
          handleSubmitEnrichDialog={handleSubmitEnrichDialog}
        />
        {isAddToFunnelDialogOpen && (
          <AddLeadListToFunnelDialog
            open={isAddToFunnelDialogOpen}
            onClose={handleCloseAddToFunnelDialog}
            leadListId={id || null}
            leadListName={data.name || ''}
            onAddToFunnel={handleActualAddToFunnel}
            leads={leads}
          />
        )}
      </Stack>
    );
  }

  // When job is done, matrix_ready, or pending, show the full data as before
  if (jobStatus === 'done' || jobStatus === 'matrix_ready' || jobStatus === 'pending') {
    if (isLoadingFull) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <PaiSpinningLoader size={'medium'} />
        </Box>
      );
    }
    if (errorFull || !apiResponse) {
      return <Alert severity="error">Error loading lead list: {errorFull instanceof Error ? errorFull.message : 'Unknown error'}</Alert>;
    }
    let enrichPhoneEmailLabel = t('enrichPhoneEmail');
    if (enrichmentStep === 'selecting') enrichPhoneEmailLabel = t('startEnrichment');
    if (enrichmentStep === 'enriching') enrichPhoneEmailLabel = t('enriching');
    const enrichPhoneEmailDisabled =
      isEnrichingLinkedIn ||
      !leads.length ||
      (enrichmentStep === 'selecting' && bcSelectedIndices.length === 0) ||
      enrichmentStep === 'enriching' ||
      removalStep !== 'idle';

    let removeContactsLabel = t('removeContacts');
    if (removalStep === 'selecting') removeContactsLabel = `Remove ${removeSelectedIndices.length} Contacts`;
    if (removalStep === 'removing') removeContactsLabel = t('removing');

    const removeContactsDisabled =
      !leads.length ||
      (removalStep === 'selecting' && removeSelectedIndices.length === 0) ||
      removalStep === 'removing' ||
      enrichmentStep !== 'idle';

    return (
      <Stack gap={2}>
        <LeadListDetailsHeader
          data={data}
          isEnriching={linkedInEnrichmentRunning}
          leads={leads}
          handleOpenFullResearchDialog={handleOpenFullResearchDialog}
          handleOpenRefinementDialog={handleOpenRefinementDialog}
          handleOpenConfirmDeleteLeadListDialog={handleOpenConfirmDeleteLeadListDialog}
          handleExportToExcel={handleExportToExcel}
          handleExportAsCSV={handleExportAsCSV}
          handleEnrichLeadList={handleEnrichLeadList}
          handleOpenAddToFunnelDialog={handleOpenAddToFunnelDialog}
          handleStartBetterContactEnrichment={handleEnrichPhoneEmailClick}
          enrichPhoneEmailLabel={enrichPhoneEmailLabel}
          enrichPhoneEmailDisabled={enrichPhoneEmailDisabled}
          handleRemoveContacts={handleRemoveContactsClick}
          removeContactsLabel={removeContactsLabel}
          removeContactsDisabled={removeContactsDisabled}
        />
        <Box sx={{ background: (theme) => theme.palette.backgrounds.main_new, padding: (theme) => theme.spacing(2, 7), borderRadius: (theme) => theme.shape.borderRadius + 'px', boxSizing: 'border-box' }}>
          {leads && leads.length > 0 ? (
            <LeadsTable
              leads={leads}
              selectionMode={enrichmentStep === 'selecting' || removalStep === 'selecting'}
              selectedIndices={enrichmentStep === 'selecting' ? bcSelectedIndices : removeSelectedIndices}
              onSelect={enrichmentStep === 'selecting' ? handleBcSelect : handleRemoveSelect}
              enrichmentStatuses={enrichmentStep === 'enriching' ? bcEnrichmentStatuses : undefined}
              showStatusColumn={enrichmentStep === 'enriching'}
              allowSelectAll={removalStep === 'selecting'}
            />
          ) : (
            <Alert severity="info">No lead data available for this list.</Alert>
          )}
        </Box>
        <RefineLeadListDialog
          open={refinementDialogOpen}
          id={id}
          isRefining={isRefining}
          refinementFeedback={refinementFeedback}
          autoSave={autoSave}
          editableProduct={editableProduct}
          editableRegion={editableRegion}
          editablePrompt={editablePrompt}
          refineRegionSplitInstructions={refineRegionSplitInstructions}
          refineSubsearchInstructions={refineSubsearchInstructions}
          peopleSearchInstructions={peopleSearchInstructions}
          setEditableProduct={setEditableProduct}
          setEditableRegion={setEditableRegion}
          setEditablePrompt={setEditablePrompt}
          setRefineRegionSplitInstructions={setRefineRegionSplitInstructions}
          setRefineSubsearchInstructions={setRefineSubsearchInstructions}
          setPeopleSearchInstructions={setPeopleSearchInstructions}
          setRefinementFeedback={setRefinementFeedback}
          setAutoSave={setAutoSave}
          handleCloseRefinementDialog={handleCloseRefinementDialog}
          handleSubmitRefinement={handleSubmitRefinement}
        />
        <FullResearchDialog
          open={fullResearchDialogOpen}
          handleCloseFullResearchDialog={handleCloseFullResearchDialog}
          handleSubmitFullResearch={handleSubmitFullResearch}
          isProcessingFull={isGeneratingMatrix}
          editableProduct={editableProduct}
          editableRegion={editableRegion}
          editablePrompt={editablePrompt}
          regionSplitInstructions={refineRegionSplitInstructions}
          subsearchInstructions={refineSubsearchInstructions}
          numRegions={numRegions}
          numSubsearches={numSubsearches}
          peopleSearchInstructions={peopleSearchInstructions}
          numPeopleSearches={numPeopleSearches}
          setEditableProduct={setEditableProduct}
          setEditableRegion={setEditableRegion}
          setEditablePrompt={setEditablePrompt}
          setNumRegions={setNumRegions}
          setNumSubsearches={setNumSubsearches}
          setPeopleSearchInstructions={setPeopleSearchInstructions}
          setNumPeopleSearches={setNumPeopleSearches}
          setRefineRegionSplitInstructions={setRefineRegionSplitInstructions}
          setRefineSubsearchInstructions={setRefineSubsearchInstructions}
          onProcessFullDirect={handleProcessFullDirect}
          isProcessingFullDirect={isProcessingFullDirect}
        />
        <PromptApprovalDialog
          open={promptApprovalOpen}
          onClose={() => setPromptApprovalOpen(false)}
          onStart={handleStartFullResearch}
          onBack={handleBackToFullResearch}
          approvedSubregions={approvedSubregions}
          approvedSubsearches={approvedSubsearches}
          approvedPeopleSearches={approvedPeopleSearches}
          onChangeSubregion={handleChangeSubregion}
          onChangeSubsearch={handleChangeSubsearch}
          onChangePeopleSearch={handleChangePeopleSearch}
          onAddSubregion={handleAddSubregion}
          onAddSubsearch={handleAddSubsearch}
          onAddPeopleSearch={handleAddPeopleSearch}
          onRemoveSubregion={handleRemoveSubregion}
          onRemoveSubsearch={handleRemoveSubsearch}
          onRemovePeopleSearch={handleRemovePeopleSearch}
          loading={isUpdatingMatrix}
        />
        <ConfirmDeleteLeadListDialog
          open={deleteConfirmOpen}
          loading={false}
          handleClose={handleCloseConfirmDeleteLeadListDialog}
          handleConfirmDelete={handleConfirmDelete}
        />
        <LeadListEnrichmentDialog
          open={enrichDialogOpen}
          isEnriching={linkedInEnrichmentRunning}
          customField={customField}
          customFieldPrompt={customFieldPrompt}
          enrichError={enrichLinkedInError?.message || null}
          setCustomField={setCustomField}
          setCustomFieldPrompt={setCustomFieldPrompt}
          handleCloseEnrichDialog={handleCloseEnrichDialog}
          handleSubmitEnrichDialog={handleSubmitEnrichDialog}
        />
        {isAddToFunnelDialogOpen && (
          <AddLeadListToFunnelDialog
            open={isAddToFunnelDialogOpen}
            onClose={handleCloseAddToFunnelDialog}
            leadListId={id || null}
            leadListName={data.name || ''}
            onAddToFunnel={handleActualAddToFunnel}
            leads={leads}
          />
        )}
      </Stack>
    );
  }

  // When job is in progress, show a loading spinner overlay in the table area
  if (jobStatus === 'in_progress') {
    return (
      <Stack gap={2}>
        <LeadListDetailsHeader
          data={data}
          isEnriching={false}
          leads={leads}
          handleOpenFullResearchDialog={handleOpenFullResearchDialog}
          handleOpenRefinementDialog={handleOpenRefinementDialog}
          handleOpenConfirmDeleteLeadListDialog={handleOpenConfirmDeleteLeadListDialog}
          handleExportToExcel={handleExportToExcel}
          handleExportAsCSV={handleExportAsCSV}
          handleEnrichLeadList={handleEnrichLeadList}
          handleOpenAddToFunnelDialog={handleOpenAddToFunnelDialog}
          handleStartBetterContactEnrichment={handleEnrichPhoneEmailClick}
          enrichPhoneEmailLabel={t('enrichPhoneEmail')}
          enrichPhoneEmailDisabled={true}
          handleRemoveContacts={handleRemoveContactsClick}
          removeContactsLabel={t('removeContacts')}
          removeContactsDisabled={true}
        />
        <Box
          sx={{
            background: (theme) => theme.palette.backgrounds.main_new,
            padding: (theme) => theme.spacing(2, 7),
            borderRadius: (theme) => theme.shape.borderRadius + 'px',
            boxSizing: 'border-box',
            minHeight: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PaiSpinningLoader size={'medium'} />
        </Box>
      </Stack>
    );
  }

  // Fallback: unknown status
  return <Alert severity="warning">Unknown research status. Please try again later.</Alert>;
};

export default LeadListStorageDetail;
